'use client';

import PropTypes from 'prop-types';
import { useState, useCallback } from 'react';

import {
  Box,
  Menu,
  Stack,
  Button,
  Tooltip,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';

import { track } from 'src/lib/analytics/client';
import { RADIUS, SHADOW, SURFACE } from 'src/theme/ui-tokens';
import { bookmarkKindOf } from 'src/constants/bookmark-sources';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

import { useBookmark } from 'src/sections/common/bookmark/use-bookmark-hook';
import { useDemoActionGuard } from 'src/sections/common/demo/use-demo-guard';

import { useAuthContext } from 'src/auth/hooks';


const DEMO_BOOKMARK_SAVE_MSG = '데모에서는 북마크를 저장할 수 없어요';
const DEMO_BOOKMARK_NEW_FOLDER_MSG = '데모에서는 폴더를 만들 수 없어요';

/**
 * 북마크 폴더는 BookmarkProvider(onSnapshot) 전역 상태와 연동됩니다.
 * 활동 로그: 폴더에 담으면 `bookmark_saved`(`source: 'card'` · 해제는 세지 않는다), 이 창에서 폴더를 만들면 `folder_created`(`source: 'overlay'`).
 *
 * ⚠️ **`@param` 은 «받는 것 하나»를 통째로 적는다** — `@param {object} props` 를 먼저 쓰고
 * 칸은 `props.<이름>` 으로 단다. 구조분해한 칸 이름을 그대로 `@param placement` 로 적으면
 * TS 가 그것을 **props 객체 전체의 타입**으로 읽어 소비처 `.tsx` 가 전부 빨개진다.
 *
 * @param {object} props
 * @param {object} props.post
 * @param {'postCard'|'postList'|'postTable'} [props.placement='postCard']
 *   - postCard: PostCard (날짜 행)
 *   - postList: PostCardList (날짜 행)
 *   - postTable: PostTable (테이블 셀)
 * @param {'soft'|'contained'} [props.buttonVariant='soft']
 *   placement이 postCard일 때 트리거 버튼 스타일(썸네일 등 어두운 배경에는 contained 권장)
 * @param {boolean} [props.disabled=false]
 * @param {string} [props.disabledTooltip='']
 * @param {object} [props.sx]
 */
export default function PostCardBookmarkOverlay({
  post,
  placement = 'postCard',
  buttonVariant = 'soft',
  disabled = false,
  disabledTooltip = '',
  sx,
}: {
  post: { id?: unknown; source?: string; platform?: string } & Record<string, unknown>
  placement?: 'postCard' | 'postList' | 'postTable'
  buttonVariant?: 'soft' | 'contained'
  disabled?: boolean
  disabledTooltip?: string
  sx?: Record<string, unknown>
}) {
  const { user } = useAuthContext();
  const workspaceId = user?.lastWorkspace ?? '';
  const userId = user?.uid ?? '';
  const canUseBookmark = Boolean(workspaceId?.trim() && userId);
  const { wrap } = useDemoActionGuard();

  const {
    folders,
    createFolder,
    addPostToFolder,
    removePostFromFolder,
    isPostBookmarked,
    isPostBookmarkedInFolder,
  } = useBookmark();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [savingFolderId, setSavingFolderId] = useState<string | null>(null);
  const [savedFolderIds, setSavedFolderIds] = useState<Set<string>>(() => new Set());

  const handleOpen = useCallback((e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
    if (disabled) {
      e.stopPropagation()
      e.preventDefault()
      return
    }
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setNewFolderName('');
    setSavedFolderIds(new Set());
  }, []);

  const handleToggleFolder = useCallback(
    async (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, folderId: string) => {
      e.stopPropagation();
      const postId = post?.id != null ? String(post.id) : '';
      if (!postId || !canUseBookmark || savingFolderId) return;

      const isSaved =
        savedFolderIds.has(folderId) || isPostBookmarkedInFolder(postId, folderId);

      setSavingFolderId(folderId);
      try {
        if (isSaved) {
          await removePostFromFolder(folderId, postId);
          setSavedFolderIds((prev) => {
            const next = new Set(prev);
            next.delete(folderId);
            return next;
          });
          toast.success('북마크가 해제되었습니다.');
        } else {
          const source = post?.source ?? post?.platform ?? '';
          await addPostToFolder(folderId, postId, source);
          setSavedFolderIds((prev) => new Set(prev).add(folderId));
          toast.success('저장되었습니다!');
          // 담았을 때만 남긴다 — 해제는 세지 않는다
          track(
            { name: 'bookmark_saved', type: 'action', props: { item_type: bookmarkKindOf({ source }), count: 1, source: 'card' } },
            { uiId: 'bookmark.overlay.save' }
          );
        }
      } catch (err) {
        toast.error(
          (err as { message?: string })?.message ?? (isSaved ? '북마크 해제에 실패했습니다.' : '저장에 실패했습니다.')
        );
      } finally {
        setSavingFolderId(null);
      }
    },
    [
      post,
      canUseBookmark,
      savingFolderId,
      savedFolderIds,
      addPostToFolder,
      removePostFromFolder,
      isPostBookmarkedInFolder,
    ]
  );

  const handleCreateFolder = useCallback(
    async (e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
      e.stopPropagation();
      const name = newFolderName.trim();
      if (!name || creatingFolder) return;
      if (!canUseBookmark) {
        toast.error('워크스페이스와 로그인이 필요합니다.');
        return;
      }
      setCreatingFolder(true);
      try {
        await createFolder({ title: name, description: '', emoji: '' });
        setNewFolderName('');
        toast.success('폴더가 만들어졌습니다.');
        track({ name: 'folder_created', type: 'action', props: { source: 'overlay' } }, { uiId: 'bookmark.overlay.new-folder' });
      } catch (err) {
        toast.error((err as { message?: string })?.message ?? '폴더 생성에 실패했습니다.');
      } finally {
        setCreatingFolder(false);
      }
    },
    [newFolderName, creatingFolder, canUseBookmark, createFolder]
  );

  const folderList = Array.isArray(folders) ? folders : [];
  const postId = post?.id != null ? String(post.id) : '';
  const alreadyBookmarkedInWorkspace = isPostBookmarked(postId);
  const isContainedTrigger = placement === 'postCard' && buttonVariant === 'contained';
  const tooltipTitle = disabledTooltip || '';
  const shouldShowDisabledTooltip = Boolean(disabled && tooltipTitle)

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'auto',
          ...sx,
        }}
      >
        {placement === 'postCard' && (
          <Tooltip title={shouldShowDisabledTooltip ? tooltipTitle : ''} disableHoverListener={!shouldShowDisabledTooltip}>
            <span>
              <Button
                className="bookmark-overlay-trigger"
                size="small"
                disabled={disabled}
                onClick={handleOpen}
                color={alreadyBookmarkedInWorkspace ? 'warning' : 'success'}
                variant={isContainedTrigger ? 'contained' : 'soft'}
                aria-label={alreadyBookmarkedInWorkspace ? '저장됨 — 폴더 변경' : '북마크'}
                sx={{
                  borderRadius: RADIUS.sm,
                  py: 0.1,
                  pr: 1,
                  pl: 0.7,
                  minWidth: 0,
                  minHeight: 0,
                  height: isContainedTrigger ? '24px' : '22px',
                  fontSize: '11px',
                  ...(disabled ? { opacity: 0.5 } : {}),
                  ...(isContainedTrigger
                    ? {
                      boxShadow: SHADOW.raised,
                      '&:hover': { boxShadow: SHADOW.overlay },
                    }
                    : {}),
                }}
              >
                <Iconify
                  icon={alreadyBookmarkedInWorkspace ? 'solar:bookmark-linear' : 'solar:bookmark-linear'}
                  width={14}
                  sx={{ mr: 0.3 }}
                />
                {alreadyBookmarkedInWorkspace ? '저장됨' : '북마크'}
              </Button>
            </span>
          </Tooltip>
        )}
        {/*
          ⚠️ 줄과 표의 방아쇠는 **같은 한 꼴**이다(22×22 알약 · 면 없음) — 줄만 「북마크」 글자 칩이라
            59px 을 먹어서, 옆에 선 다른 동작 아이콘과 크기도 세로 줄도 맞지 않았다.
          🔑 **상태는 색이 진다** — 안 담음은 흐리게, 담았으면 초록, 다른 폴더에 이미 있으면 노랑.
            말은 `aria-label` 과 풍선이 나른다.
        */}
        {(placement === 'postList' || placement === 'postTable') && (
          <Tooltip title={shouldShowDisabledTooltip ? tooltipTitle : ''} disableHoverListener={!shouldShowDisabledTooltip}>
            <span>
              <Button
                className="bookmark-overlay-trigger"
                size="small"
                disabled={disabled}
                onClick={handleOpen}
                color={alreadyBookmarkedInWorkspace ? 'warning' : 'success'}
                variant="text"
                aria-label={alreadyBookmarkedInWorkspace ? '저장됨 — 폴더 변경' : '북마크'}
                sx={{
                  borderRadius: RADIUS.sm,
                  p: 0,
                  minWidth: 22,
                  minHeight: 0,
                  width: 22,
                  height: 22,
                  color: alreadyBookmarkedInWorkspace ? undefined : 'text.disabled',
                  '&:hover': { bgcolor: SURFACE.hover },
                  ...(disabled ? { opacity: 0.5 } : {}),
                }}
              >
                <Iconify icon="solar:bookmark-linear" width={14} />
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          root: { sx: { zIndex: 13050 } },
          paper: {
            sx: {
              mt: 0.5,
              zIndex: 13050,
              minWidth: 280,
              maxWidth: 360,
              overflow: 'hidden',
              p: 0,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: SHADOW.overlay,
            },
          },
        }}
        keepMounted={false}
      >
        <Box
          sx={{ p: 1.5 }}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >


          {!canUseBookmark && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              워크스페이스 선택 및 로그인 후 북마크를 사용할 수 있습니다.
            </Typography>
          )}

          <Stack
            spacing={0}
            sx={{
              maxHeight: 200,
              overflowY: 'auto',
              bgcolor: 'grey.50',
              borderRadius: RADIUS.sm,
            }}
          >
            {folderList.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.5 }}>
                폴더가 없습니다. 아래에서 새 폴더를 만드세요.
              </Typography>
            ) : (
              folderList.map((folder) => {
                const fid = folder.id;
                const label = folder.title ?? folder.id;
                const isSaving = savingFolderId === fid;
                const isSaved =
                  savedFolderIds.has(fid) || isPostBookmarkedInFolder(postId, fid);
                return (
                  <Box
                    key={fid}
                    role="button"
                    tabIndex={0}
                    onClick={(e) =>
                      wrap(() => handleToggleFolder(e, fid), DEMO_BOOKMARK_SAVE_MSG)()
                    }
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        wrap(() => handleToggleFolder(e, fid), DEMO_BOOKMARK_SAVE_MSG)();
                      }
                    }}
                    sx={{
                      m: 0,
                      px: 1.5,
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: canUseBookmark && postId && !isSaving ? 'pointer' : 'default',
                      opacity: canUseBookmark && postId && !isSaving ? 1 : 0.6,
                      ...(isSaved && {
                        bgcolor: 'primary.lighter',
                        color: 'primary.darker',
                      }), 
                      '&:hover': {
                        bgcolor:
                          canUseBookmark && postId && !isSaving
                            ? isSaved
                              ? 'primary.light'
                              : 'action.hover'
                            : undefined,
                      },
                    }}
                  >
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {label}
                    </Typography>
                    {isSaving && <CircularProgress size={16} />}
                  </Box>
                );
              })
            )}
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              mt: 1,
              borderRadius: RADIUS.sm,
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="새 폴더 이름"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') handleCreateFolder(e);
              }}
              variant="standard"
              InputProps={{ disableUnderline: true }}
              disabled={!canUseBookmark || creatingFolder}
              sx={{
                '& .MuiInputBase-root': {
                  py: 0.1,
                  minHeight: 0,
                  Height: '24px',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: RADIUS.sm,
                  bgcolor: 'white',
                  px: 1,
                  '&:hover': { borderColor: 'grey.400' },
                  '&.Mui-focused': { borderColor: 'primary.main', borderWidth: 1 },
                },
                '& .MuiInputBase-input': { typography: 'uiBody', py: 0.25 },
                '& .MuiInputBase-input::placeholder': { typography: 'uiBody', opacity: 1 },
              }}
            />
            <Button
              size="small"
              variant="contained"
              color='inherit'
              onClick={(e) => {
                e.stopPropagation();
                wrap(handleCreateFolder, DEMO_BOOKMARK_NEW_FOLDER_MSG)(e);
              }}
              disabled={!canUseBookmark || !newFolderName.trim() || creatingFolder}
              sx={{
                flexShrink: 0,
                minWidth: 44,
                py: 0.25,
                typography: 'uiBody',
              }}
              startIcon={creatingFolder ? <CircularProgress size={14} /> : null}
            >
              추가
            </Button>
          </Stack>
        </Box>
      </Menu>
    </>
  );
}

PostCardBookmarkOverlay.propTypes = {
  post: PropTypes.object.isRequired,
  placement: PropTypes.oneOf(['postCard', 'postList', 'postTable']),
  buttonVariant: PropTypes.oneOf(['soft', 'contained']),
  sx: PropTypes.object,
};
